import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Cloud as CloudIcon,
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (role) {
      case 'ADMIN':
        return 'warning';
      case 'CLIENT':
        return 'info';
      case 'USER':
        return 'success';
      default:
        return 'primary';
    }
  };

  const getChangePasswordUrl = (): string => {
    switch (user?.role) {
      case 'ADMIN':
        return '/admin/change-password';
      case 'CLIENT':
        return '/client/change-password';
      case 'USER':
        return '/user/change-password';
      default:
        return '';
    }
  };

  const handleOpenPasswordDialog = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('auth.passwordMin'));
      return;
    }

    setLoading(true);
    try {
      await api.put(getChangePasswordUrl(), {
        currentPassword,
        newPassword,
      });
      setSuccess(t('auth.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setPasswordDialogOpen(false);
      }, 1500);
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.error;
      if (message && message.includes('incorrect')) {
        setError(t('auth.currentPasswordWrong'));
      } else {
        setError(t('auth.passwordChangeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        }}
      >
        <Toolbar>
          <CloudIcon sx={{ mr: 1.5, fontSize: 28 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            {t('app.name')}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 20, opacity: 0.8 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user.name}
                </Typography>
              </Box>
              <Chip
                label={user.role}
                color={getRoleColor(user.role)}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
              <Tooltip title={t('auth.changePassword')}>
                <IconButton
                  color="inherit"
                  onClick={handleOpenPasswordDialog}
                  sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}
                >
                  <LockIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <IconButton
                color="inherit"
                onClick={() => {
                  const newLang = i18n.language === 'ar' ? 'en' : 'ar';
                  i18n.changeLanguage(newLang);
                  localStorage.setItem('language', newLang);
                }}
                sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  {i18n.language === 'ar' ? 'EN' : '\u0639\u0631'}
                </Typography>
              </IconButton>
              <Tooltip title={t('navbar.logout')}>
                <IconButton
                  color="inherit"
                  onClick={handleLogout}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                    },
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Dialog
        open={passwordDialogOpen}
        onClose={handleClosePasswordDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('auth.changePassword')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            <TextField
              label={t('auth.currentPassword')}
              type="password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
            />
            <TextField
              label={t('auth.newPassword')}
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
            />
            <TextField
              label={t('auth.confirmPassword')}
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
          >
            {loading ? <CircularProgress size={24} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
